import { useState } from 'react';
import { GitMerge, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function authHeaders() {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('olympus_token')}` };
}

export function MergePanel({ duplicates, onMergeComplete }) {
  const [merging, setMerging] = useState(false);

  const handleMerge = async (group) => {
    if (group.ids.length < 2) return;
    setMerging(true);
    try {
      const res = await fetch(`${API_URL}/api/institutionen/merge`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ haupt_id: group.ids[0], duplikat_ids: group.ids.slice(1), migrate_verknuepfungen: true }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { detail: text }; }
      if (!res.ok) throw new Error(data.detail || 'Merge fehlgeschlagen');
      toast.success(`${data.merged_count} Dublette(n) zusammengeführt, ${data.verknuepfungen_migriert} Verknüpfungen migriert`);
      onMergeComplete();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMerging(false);
    }
  };

  if (!duplicates || duplicates.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="merge-panel-empty">
        <Check className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
        <p className="text-slate-600 font-medium">Keine Dubletten gefunden</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4" data-testid="merge-panel">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium text-amber-800">{duplicates.length} Dubletten-Gruppen</span>
      </div>
      {duplicates.map((group, idx) => (
        <Card key={idx} className="border-amber-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm text-slate-800">{group.normalized_name || group._id}</span>
              <Badge variant="outline" className="text-amber-600">{group.count}x</Badge>
            </div>
            <div className="space-y-1 mb-3">
              {(group.names || []).map((name, i) => (
                <div key={i} className="text-xs flex items-center gap-2 text-slate-600">
                  <Badge className={i === 0 ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-red-100 text-red-700 text-[10px]"}>
                    {i === 0 ? 'Haupt' : 'Dublette'}
                  </Badge>
                  {name}
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => handleMerge(group)} disabled={merging}
              className="w-full text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`merge-btn-${idx}`}>
              {merging ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <GitMerge className="w-3 h-3 mr-1.5" />}
              Zusammenführen
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
